import { Command, Extension } from '@tiptap/core';
import { AllSelection, TextSelection, Transaction } from 'prosemirror-state';

export interface IndentOptions {
  types: string[];
  minLevel: number;
  maxLevel: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    indent: {
      indent: () => ReturnType;
      indentTab: () => ReturnType;
      outdent: () => ReturnType;
      outdentBackSpace: () => ReturnType;
    };
  }
}

export const Indent = Extension.create<IndentOptions>({
  name: 'indent',

  addOptions() {
    return {
      types: ['listItem', 'paragraph', "heading"],
      minLevel: 0,
      maxLevel: 4,
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            renderHTML: attributes => {
              return attributes?.indent > this.options.minLevel ? { 'data-indent': attributes.indent } : null;
            },
            parseHTML: element => {
              const level = Number(element.getAttribute('data-indent'));
              return level && level > this.options.minLevel ? level : null;
            },
          },
        },
      },
    ];
  },
  addCommands() {
    const setNodeIndentMarkup = (tr: Transaction, pos: number, delta: number): Transaction => {
      const node = tr?.doc?.nodeAt(pos);

      if (node) {
        const nextLevel = (node.attrs.indent || 0) + delta;
        const { minLevel, maxLevel } = this.options;
        const indent = nextLevel < minLevel ? minLevel : nextLevel > maxLevel ? maxLevel : nextLevel;

        if (indent !== node.attrs.indent) {
          const { indent: oldIndent, ...currentAttrs } = node.attrs;
          const nodeAttrs = indent > minLevel ? { ...currentAttrs, indent } : currentAttrs;
          return tr.setNodeMarkup(pos, node.type, nodeAttrs, node.marks);
        }
      }
      return tr;
    };

    const updateIndentLevel = (tr: Transaction, delta: number): Transaction => {
      const { doc, selection } = tr;

      if (doc && selection && (selection instanceof TextSelection || selection instanceof AllSelection)) {
        const { from, to } = selection;
        doc.nodesBetween(from, to, (node, pos) => {
          if (delta === 0 && (node.attrs.indent || 0) == 0) {
            return true;
          }
          if (this.options.types.includes(node.type.name)) {
            tr = setNodeIndentMarkup(tr, pos, delta || -1);
            return false;
          }

          return true;
        });
      }

      return tr;
    };
    const applyIndent: (delta: number) => () => Command =
      (delta) =>
        () =>
          ({ tr, state, dispatch }) => {
            const { selection } = state;
            if (delta === 2 && (selection.$anchor.parentOffset > 0 || selection.from !== selection.to))
              return false
            if (delta === 0 && (selection.$anchor.parentOffset > 0 || selection.from !== selection.to))
              return false
            tr = tr.setSelection(selection);
            tr = updateIndentLevel(tr, delta);

            if (tr.docChanged) {
              dispatch?.(tr);
              return true;
            }

            return false;
          };

    return {
      indent: applyIndent(1),
      indentTab: applyIndent(2),
      outdent: applyIndent(-1),
      outdentBackSpace: applyIndent(0),
    };
  },

  addKeyboardShortcuts() {
    return {
      Tab: () => this.editor.commands.indentTab(),
      'Shift-Tab': () => this.editor.commands.outdent(),
      Backspace: () => this.editor.commands.outdentBackSpace()
    };
  },
});